import { useState } from "react";
import { ActionIcon, Badge, Button, Code, Collapse, Group, Input, NumberInput, Paper, SegmentedControl, Stack, Table, Text, ThemeIcon } from "@mantine/core";
import { IconAlertTriangle, IconCheck, IconChevronDown, IconChevronRight, IconPlus, IconX } from "@tabler/icons-react";
import { calculateFromRanges, ctcRangeIndexFor, ctcRangesCheck } from "../../../../shared/ctcCommission";
import type { CtcCommissionRange } from "../../types";

/**
 * Commission profile ranges as a table instead of raw JSON: one row per range (From, To, Fixed / %, value,
 * minimum), "+ Add range", the same checks the form saves with, and "Try an amount" computed with the
 * server's own calculation (shared/ctcCommission.ts). Saves the same `ranges` array as before.
 */
type Row = Partial<CtcCommissionRange>;

/** A range as stored: only the keys that apply (Firestore can't hold undefined). */
const clean = (r: Row): Row => {
    const out: Row = { startAmount: r.startAmount ?? 0, isFixedAmount: !!r.isFixedAmount };
    if (r.endAmount !== undefined && r.endAmount !== null) out.endAmount = r.endAmount;
    if (r.isFixedAmount) { if (r.fixedAmount !== undefined) out.fixedAmount = r.fixedAmount; } else if (r.percentage !== undefined) out.percentage = r.percentage;
    // A minimum only means something for a % charge (a fixed charge is always exactly its amount)
    if (!r.isFixedAmount && r.minimumAmount !== undefined && r.minimumAmount !== null) out.minimumAmount = r.minimumAmount;
    return out;
};
const num = (v: string | number) => (v === "" || v === undefined ? undefined : Number(v));
const fmt = (n: number, digits = 3) => n.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });

export function CtcRangesEditor({ label, value, onChange, currency, isOnRange, error }: {
    label: string; value: unknown; onChange: (ranges: Row[]) => void; currency?: string; isOnRange?: boolean; error?: string;
}) {
    const rows: Row[] = Array.isArray(value) ? (value as Row[]) : [];
    const unreadable = value !== undefined && value !== null && value !== "" && (!Array.isArray(value) || rows.some((r) => typeof r !== "object" || r === null || typeof r.startAmount !== "number"));
    const [tryAmount, setTryAmount] = useState<number | string>("");
    const [showJson, setShowJson] = useState(false);
    const cur = currency || "";
    // Every range after the first starts at the previous "To" + 1, so an amount is always in exactly one range
    const chain = (list: Row[]) => list.map((r, j) => {
        const prevEnd = j > 0 ? list[j - 1].endAmount : undefined;
        return clean(j > 0 && prevEnd !== undefined && prevEnd !== null ? { ...r, startAmount: prevEnd + 1 } : r);
    });
    const set = (i: number, patch: Row) => onChange(chain(rows.map((r, j) => (j === i ? { ...r, ...patch } : r))));
    const add = () => {
        const last = rows[rows.length - 1];
        // The new range starts at the last "To" + 1 (0 – 100,000, then 100,001 – …)
        onChange(chain([...rows, { startAmount: last ? (last.endAmount ?? last.startAmount ?? 0) + 1 : 0, isFixedAmount: false, minimumAmount: 0 }]));
    };
    const remove = (i: number) => onChange(chain(rows.filter((_, j) => j !== i)));
    const { errors, warnings } = ctcRangesCheck(rows);

    const amount = typeof tryAmount === "number" ? tryAmount : undefined;
    const tryIndex = amount !== undefined ? ctcRangeIndexFor(amount, rows as CtcCommissionRange[]) : -1;
    const tryResult = amount !== undefined && !errors.length ? calculateFromRanges(amount, { isOnRange: !!isOnRange, ranges: rows as CtcCommissionRange[] }) : undefined;
    const tryRange = tryIndex >= 0 ? rows[tryIndex] : undefined;
    // How the result was reached, in words (same rules as calculateFromRanges)
    const tryExplain = (() => {
        if (amount === undefined) return "";
        const reached = rows.filter((r) => (r.startAmount ?? 0) <= amount);
        if (reached.length > 1 && reached.every((r) => !r.isFixedAmount) && !isOnRange) {
            // Each part at its own range's % (same split as calculateFromRanges: a part runs up to the next range's "From")
            const sorted = [...rows].sort((a, b) => (a.startAmount ?? 0) - (b.startAmount ?? 0));
            let remaining = amount - (sorted[0].startAmount ?? 0);
            const parts: string[] = [];
            for (let k = 0; k < sorted.length && remaining > 0; k++) {
                const r = sorted[k];
                if ((r.startAmount ?? 0) > amount) break;
                const next = sorted[k + 1];
                const width = (next ? next.startAmount ?? 0 : r.endAmount ?? Number.POSITIVE_INFINITY) - (r.startAmount ?? 0);
                const part = Math.min(remaining, width);
                const raw = part * ((r.percentage ?? 0) / 100);
                const charged = r.minimumAmount !== undefined && r.minimumAmount !== null && raw <= r.minimumAmount ? r.minimumAmount : raw;
                parts.push(`range ${rows.indexOf(r) + 1}: ${fmt(part, 0)} × ${r.percentage ?? 0} % = ${fmt(charged)}${charged !== raw ? " (minimum)" : ""}`);
                remaining -= part;
            }
            return `→ each part at its range's %: ${parts.join(" + ")}`;
        }
        if (!tryRange) return "→ in no range, so no commission";
        if (tryRange.isFixedAmount) return `→ range ${tryIndex + 1} · fixed amount`;
        const raw = amount * ((tryRange.percentage ?? 0) / 100);
        const min = tryRange.minimumAmount;
        const calc = `→ range ${tryIndex + 1} · ${fmt(amount)} × ${tryRange.percentage ?? 0} % = ${fmt(raw)}`;
        return min !== undefined && min !== null && raw <= min ? `${calc}, below the minimum ${fmt(min)}, so the minimum applies` : calc;
    })();

    return (
        <Input.Wrapper label={label} required error={error}>
            <Paper withBorder radius="md" p="sm" mt={4}>
                {unreadable && <Text size="xs" c="red" mb="xs">The saved ranges can't be read (old or hand-typed data). Remove them and enter the ranges again.</Text>}
                <Text size="xs" c="dimmed" mb="xs">
                    Order amount{cur ? ` in ${cur}` : ""}. Enter each range's “To” (empty = no limit, last range only); the next range's “From” is filled in as “To” + 1, so every amount is in exactly one range.
                </Text>
                <Table.ScrollContainer minWidth={820}>
                    <Table verticalSpacing={4} horizontalSpacing={6} withRowBorders={false}>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th w={40} />
                                <Table.Th>From{cur ? ` (${cur})` : ""}</Table.Th>
                                <Table.Th>To{cur ? ` (${cur})` : ""}</Table.Th>
                                <Table.Th>Charge</Table.Th>
                                <Table.Th>Value</Table.Th>
                                <Table.Th>Minimum</Table.Th>
                                <Table.Th w={32} />
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {rows.map((r, i) => (
                                <Table.Tr key={i}>
                                    <Table.Td><ThemeIcon variant="light" size={26} radius="md"><Text size="xs" fw={700}>{i + 1}</Text></ThemeIcon></Table.Td>
                                    <Table.Td><NumberInput size="xs" miw={140} value={r.startAmount ?? ""} onChange={(v) => set(i, { startAmount: num(v) })} min={0} thousandSeparator="," decimalScale={3} hideControls
                                        disabled={i > 0} title={i > 0 ? `Always range ${i}'s "To" + 1` : undefined} /></Table.Td>
                                    <Table.Td><NumberInput size="xs" miw={140} value={r.endAmount ?? ""} placeholder="no limit" onChange={(v) => set(i, { endAmount: num(v) })} min={0} thousandSeparator="," decimalScale={3} hideControls /></Table.Td>
                                    <Table.Td>
                                        <SegmentedControl size="xs" value={r.isFixedAmount ? "fixed" : "pct"} onChange={(v) => set(i, { isFixedAmount: v === "fixed" })}
                                            data={[{ value: "fixed", label: "Fixed" }, { value: "pct", label: "%" }]} />
                                    </Table.Td>
                                    <Table.Td>
                                        {r.isFixedAmount ?
                                            <NumberInput size="xs" value={r.fixedAmount ?? ""} onChange={(v) => set(i, { fixedAmount: num(v) })} min={0} thousandSeparator="," decimalScale={3} hideControls rightSection={<Text size="xs" c="dimmed">{cur}</Text>} rightSectionWidth={40} /> :
                                            <NumberInput size="xs" value={r.percentage ?? ""} onChange={(v) => set(i, { percentage: num(v) })} min={0} decimalScale={6} hideControls rightSection={<Text size="xs" c="dimmed">%</Text>} />}
                                    </Table.Td>
                                    <Table.Td><NumberInput size="xs" value={r.isFixedAmount ? "" : r.minimumAmount ?? ""} onChange={(v) => set(i, { minimumAmount: num(v) })} min={0} thousandSeparator="," decimalScale={3} hideControls
                                        disabled={!!r.isFixedAmount} placeholder={r.isFixedAmount ? "—" : undefined} title={r.isFixedAmount ? "Only for a % charge" : undefined}
                                        rightSection={<Text size="xs" c="dimmed">{cur}</Text>} rightSectionWidth={40} /></Table.Td>
                                    <Table.Td><ActionIcon variant="subtle" color="red" onClick={() => remove(i)} aria-label={`Remove range ${i + 1}`}><IconX size={16} /></ActionIcon></Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Table.ScrollContainer>
                <Button size="compact-sm" variant="light" leftSection={<IconPlus size={14} />} mt="xs" onClick={add}>
                    Add range{rows.length && rows[rows.length - 1].endAmount !== undefined && rows[rows.length - 1].endAmount !== null ? ` (starts at ${fmt((rows[rows.length - 1].endAmount ?? 0) + 1, 0)})` : ""}
                </Button>

                <Stack gap={2} mt="xs">
                    {errors.map((e) => <Group key={e} gap={6} wrap="nowrap"><IconX size={14} color="var(--mantine-color-red-6)" /><Text size="xs" c="red">{e}</Text></Group>)}
                    {/* Every problem blocks Save, so all show in red */}
                    {warnings.map((w) => <Group key={w} gap={6} wrap="nowrap"><IconAlertTriangle size={14} color="var(--mantine-color-red-6)" /><Text size="xs" c="red">{w}</Text></Group>)}
                    {!errors.length && !warnings.length && rows.length > 0 && <Group gap={6}><IconCheck size={14} color="var(--mantine-color-green-6)" /><Text size="xs" c="green.8">Ranges run from {fmt(rows.reduce((m, r) => Math.min(m, r.startAmount ?? 0), Infinity), 0)} with no gaps or overlaps.</Text></Group>}
                </Stack>

                <Paper bg="gray.0" radius="md" p="sm" mt="sm">
                    <Group gap="md" wrap="wrap">
                        <Text size="sm" c="dimmed">Test with an order amount</Text>
                        <NumberInput size="xs" w={220} value={tryAmount} onChange={setTryAmount} min={0} thousandSeparator="," decimalScale={3} hideControls placeholder="e.g. 2,000,000" rightSection={<Text size="xs" c="dimmed">{cur}</Text>} rightSectionWidth={40} />
                        {amount !== undefined && (errors.length ? <Text size="sm" c="dimmed">Fix the ranges first</Text> :
                            <Text size="sm">
                                {tryExplain}{" · commission "}<Text span fw={700} c="blue.8">{fmt(tryResult ?? 0)} {cur}</Text>
                            </Text>)}
                    </Group>
                </Paper>

                <Button variant="subtle" size="compact-xs" color="gray" mt="xs" leftSection={showJson ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />} onClick={() => setShowJson((o) => !o)}>
                    Show as JSON (advanced)
                </Button>
                <Collapse expanded={showJson}><Code block mt={4}>{JSON.stringify(rows.map(clean), null, 2)}</Code></Collapse>
            </Paper>
        </Input.Wrapper>
    );
}

/** One readable line per range ("0 – 1,500,000 · 75 KWD fixed"), or null if the saved ranges can't be read. */
export const ctcRangeLines = (value: unknown, currency?: string): string[] | null => {
    if (!Array.isArray(value) || !value.length || ctcRangesCheck(value).errors.length) return null;
    const n = (x: number) => x.toLocaleString("en-US", { maximumFractionDigits: 3 });
    return (value as CtcCommissionRange[]).map((r) => {
        const span = r.endAmount === undefined || r.endAmount === null ? `${n(r.startAmount ?? 0)} +` : `${n(r.startAmount ?? 0)} – ${n(r.endAmount)}`;
        const charge = r.isFixedAmount ? `${n(r.fixedAmount ?? 0)} ${currency ?? ""} fixed`.replace("  ", " ") : `${r.percentage ?? 0} %`;
        const minimum = !r.isFixedAmount && r.minimumAmount ? ` · min ${n(r.minimumAmount)}` : "";
        return `${span} · ${charge}${minimum}`;
    });
};

/** Ranges in the Back office list: one line per range, or a red badge when the saved data can't be read. */
export function CtcRangesSummary({ value, currency }: { value: unknown; currency?: string }) {
    const lines = ctcRangeLines(value, currency);
    if (!lines) return <Badge color="red" variant="light">Invalid ranges · edit to fix</Badge>;
    return <Stack gap={0}>{lines.map((l, i) => <Text key={i} size="sm" style={{ whiteSpace: "nowrap" }}>{l}</Text>)}</Stack>;
}
